import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, KpiCard, Panel, formatEur } from "@/components/shared/PageUi";
import CrudList from "@/components/shared/CrudList";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";
import { Package, AlertTriangle, Wallet, ShoppingBag, Users } from "lucide-react";

const TX_TYPES = [
  { value: "in", label: "Sisään (osto)" },
  { value: "out", label: "Ulos (myynti)" },
  { value: "adjustment", label: "Inventaarikorjaus" },
  { value: "transfer", label: "Siirto" },
];
const PRODUCT_STATUS = [
  { value: "active", label: "Aktiivinen" },
  { value: "discontinued", label: "Poistettu" },
];
const LOC_TYPES = [
  { value: "warehouse", label: "Varasto" },
  { value: "shop", label: "Myymälä" },
  { value: "vehicle", label: "Ajoneuvo" },
];

export default function InventoryDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const r = await api.get("/inventory/stats"); setStats(r.data); }
    catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const products = {
    endpoint: "/inventory/products",
    testidPrefix: "products",
    emptyMessage: "Ei tuotteita",
    defaults: { name: "", sku: "", category: "", unit: "kpl", purchase_price: 0, selling_price: 0, vat_percent: 25.5, stock_quantity: 0, min_stock: 0, status: "active" },
    fields: [
      { key: "name", label: "Tuotteen nimi", type: "text", required: true },
      { key: "sku", label: "SKU", type: "text" },
      { key: "category", label: "Kategoria", type: "text" },
      { key: "unit", label: "Yksikkö", type: "text", placeholder: "kpl, kg, l..." },
      { key: "purchase_price", label: "Ostohinta €", type: "number" },
      { key: "selling_price", label: "Myyntihinta €", type: "number" },
      { key: "vat_percent", label: "ALV %", type: "number", step: "0.1" },
      { key: "stock_quantity", label: "Varastosaldo", type: "number" },
      { key: "min_stock", label: "Minimisaldo", type: "number" },
      { key: "warehouse_location", label: "Sijainti", type: "text" },
      { key: "supplier_name", label: "Toimittaja", type: "text" },
      { key: "barcode", label: "Viivakoodi", type: "text" },
      { key: "status", label: "Tila", type: "select", options: PRODUCT_STATUS },
      { key: "image_url", label: "Kuvan URL", type: "text", span: 2 },
      { key: "description", label: "Kuvaus", type: "textarea", span: 2 },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "sku", label: "SKU" },
      { key: "name", label: "Tuote" },
      { key: "category", label: "Kategoria" },
      { key: "stock_quantity", label: "Saldo", format: "num", digits: 2, align: "right" },
      { key: "selling_price", label: "Hinta", format: "eur", align: "right" },
      { key: "status", label: "Tila", format: "badge" },
    ],
    getRowLabel: (p) => p.name,
  };

  const transactions = {
    endpoint: "/inventory/transactions",
    testidPrefix: "tx",
    emptyMessage: "Ei varastotapahtumia",
    defaults: { product_id: "", product_name: "", type: "in", quantity: 0, unit_price: 0 },
    fields: [
      { key: "product_id", label: "Tuote ID", type: "text", required: true },
      { key: "product_name", label: "Tuotteen nimi", type: "text" },
      { key: "type", label: "Tapahtuman tyyppi", type: "select", options: TX_TYPES, required: true },
      { key: "quantity", label: "Määrä", type: "number", required: true },
      { key: "unit_price", label: "à-hinta €", type: "number" },
      { key: "reference_type", label: "Viittaustyyppi", type: "text", placeholder: "purchase / sale / manual" },
      { key: "reference_id", label: "Viite-ID", type: "text" },
      { key: "location", label: "Sijainti", type: "text" },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "created_at", label: "Pvm", format: "date" },
      { key: "product_name", label: "Tuote" },
      { key: "type", label: "Tyyppi", format: "badge" },
      { key: "quantity", label: "Määrä", format: "num", digits: 2, align: "right" },
      { key: "total_price", label: "Yhteensä", format: "eur", align: "right" },
    ],
  };

  const locations = {
    endpoint: "/inventory/locations",
    testidPrefix: "loc",
    emptyMessage: "Ei sijainteja",
    defaults: { name: "", type: "warehouse" },
    fields: [
      { key: "name", label: "Nimi", type: "text", required: true },
      { key: "type", label: "Tyyppi", type: "select", options: LOC_TYPES },
      { key: "address", label: "Osoite", type: "text", span: 2 },
      { key: "notes", label: "Muistiinpanot", type: "textarea", span: 2 },
    ],
    columns: [
      { key: "name", label: "Nimi" },
      { key: "type", label: "Tyyppi", format: "badge" },
      { key: "address", label: "Osoite" },
    ],
  };

  return (
    <div className="space-y-6" data-testid="inventory-dashboard">
      <PageHeader kicker="Sovellus" title="Varasto" description="Tuotteet, varastotapahtumat ja sijainnit." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<Package className="w-5 h-5" />} label="Tuotteita" value={stats?.products_total ?? "—"} loading={loading} testid="inv-kpi-products" />
        <KpiCard icon={<AlertTriangle className="w-5 h-5" />} label="Alaraja-hälytys" value={stats?.low_stock_count ?? 0} accent={(stats?.low_stock_count || 0) > 0 ? "text-red-300" : "text-emerald-300"} loading={loading} testid="inv-kpi-low" />
        <KpiCard icon={<Wallet className="w-5 h-5" />} label="Varaston arvo" value={formatEur(stats?.stock_value)} accent="text-amber-300" loading={loading} testid="inv-kpi-value" />
        <KpiCard icon={<ShoppingBag className="w-5 h-5" />} label="Myyntipotentiaali" value={formatEur(stats?.retail_value)} accent="text-emerald-300" loading={loading} testid="inv-kpi-retail" />
      </div>

      <Panel title="Hallinta">
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-amber-500/10 mb-4">
            <TabsTrigger value="products" data-testid="inv-tab-products">Tuotteet</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="inv-tab-tx">Varastotapahtumat</TabsTrigger>
            <TabsTrigger value="locations" data-testid="inv-tab-loc">Sijainnit</TabsTrigger>
            <TabsTrigger value="staff" data-testid="inv-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
          </TabsList>
          <TabsContent value="products"><CrudList config={products} /></TabsContent>
          <TabsContent value="transactions"><CrudList config={transactions} /></TabsContent>
          <TabsContent value="locations"><CrudList config={locations} /></TabsContent>
          <TabsContent value="staff"><ModuleStaffTab moduleKey="inventory" moduleLabel="Varasto" /></TabsContent>
        </Tabs>
      </Panel>
    </div>
  );
}
